import React from 'react';
import { plata, fecha } from '../utils/formato';
import { enPesos } from '../utils/letras';
import Membrete from './Membrete';
import { useConsecutivo, llaveDeRegistro } from '../utils/consecutivo';

// El comprobante de egreso, listo para imprimir.
//
// Reemplaza el talonario que llenaban a mano. Los dos datos que más se
// equivocaban a mano son los que aquí salen de la misma cifra y no pueden
// discrepar: el valor en números y «la suma en letras».
//
// Se imprime COMPLETO, no sobre el papel preimpreso. Encajar los datos en las
// casillas de un formato ya impreso obliga a calibrar cada impresora renglón
// por renglón, y se desalinea con la primera impresora distinta. Así sale igual
// desde cualquiera, y se puede guardar en PDF.
//
// Van DOS copias en la misma hoja —una para el beneficiario y otra para el
// archivo—, que es para lo que servía el papel calcado del talonario.

function Copia({ v, cual }) {
  return (
    <div className="comprobante">
      <Membrete v={v} titulo="COMPROBANTE DE EGRESO" cual={cual} />

      <table className="cuerpo-c">
        <tbody>
          <tr>
            <td className="rot">Ciudad</td>
            <td>{v.ciudad || '—'}</td>
            <td className="rot">Fecha</td>
            <td>{fecha(v.fecha)}</td>
          </tr>
          <tr>
            <td className="rot">Pagado a</td>
            <td colSpan={2} className="neg">{v.aQuien}</td>
            <td className="valor">{plata(v.monto)}</td>
          </tr>
          <tr>
            <td className="rot">C.C. o NIT</td>
            <td colSpan={3}>{v.documento || '________________________'}</td>
          </tr>
          <tr>
            <td className="rot">Por concepto de</td>
            <td colSpan={3}>{v.concepto}</td>
          </tr>
          <tr>
            {/* La suma en letras. Es la que manda si no coincide con la cifra
                —por eso el comprobante lleva las dos—, y aquí las dos salen del
                mismo número: no pueden discrepar. */}
            <td className="rot">La suma de</td>
            <td colSpan={3} className="neg">{enPesos(v.monto)}</td>
          </tr>
          <tr>
            <td className="rot">Forma de pago</td>
            <td colSpan={3}>
              {v.forma === 'transferencia' ? 'Transferencia'
                : v.forma === 'cheque' ? `Cheque No. ${v.cheque || '______'}`
                : 'Efectivo'}
            </td>
          </tr>
        </tbody>
      </table>

      {/* El detalle de cómo se llegó al neto. Sin esto, quien recibe menos de
          lo que entregó no tiene con qué comprobarlo, y esa es la discusión
          que este programa existe para evitar. */}
      {v.renglones && v.renglones.length > 0 && (
        <table className="detalle">
          <tbody>
            {v.renglones.map((r, n) => (
              <tr key={n} className={r.chico ? 'vacio' : undefined}>
                {/* Los renglones `chico` son el detalle de un préstamo: de
                    cuánto era, cuánto lleva abonado y cuánto le queda. Van
                    sangrados y sin cifra en la columna de la derecha, porque
                    NO son otra resta: si llevaran monto, alguien sumaría la
                    columna y le saldría el doble. */}
                <td style={r.chico ? { paddingLeft: 22 } : undefined}>{r.texto}</td>
                <td className="der">
                  {r.chico ? '' :
                    <>{r.signo === '-' ? '−' : ''}{plata(Math.abs(r.monto))}</>}
                </td>
              </tr>
            ))}
            <tr className="neto">
              <td>NETO PAGADO</td>
              <td className="der">{plata(v.monto)}</td>
            </tr>
          </tbody>
        </table>
      )}

      <div className="firmas">
        <div>
          <div className="linea" />
          <div className="chico">Elaborado por</div>
        </div>
        <div>
          <div className="linea" />
          <div className="chico">Firma y sello del beneficiario</div>
          <div className="chico">C.C. {v.documento || ''}</div>
        </div>
      </div>
    </div>
  );
}

// El nombre del negocio sale de la sesión guardada. Pasarlo a mano por cada
// pantalla que imprime un comprobante es una cadena que se rompe en la que se
// olvide, y el papel sale sin membrete sin que nadie lo note hasta imprimirlo.
function delNegocio() {
  try {
    const s = JSON.parse(localStorage.getItem('quesera-sesion'));
    const u = s && s.user;
    return { negocio: u?.negocio || null, nit: u?.nit || null,
             direccion: u?.direccion || null, ciudad: u?.ciudad || null,
             telefono: u?.telefono || null, logo: u?.logo || null };
  } catch { return {}; }
}

function Comprobante({ v, alCerrar }) {
  // EL CONSECUTIVO, no el id de la base.
  //
  // Lo que salía impreso era el `id` de la venta o del pago: un número
  // compartido con las otras empresas del servidor, por eso las ventas de
  // Gabriel empezaron en 102 y van saltando. Pedirle «la 45» a alguien no
  // servía: esa 45 podía no existir en su negocio.
  //
  // Va aquí y no en cada pantalla que abre este papel —al vender, al pagar,
  // y al sacarlo del archivo— porque una cadena de cinco sitios se rompe en
  // el que se olvide. Antes del `return null`, por el orden de los
  // enganches de React.
  const consecutivo = useConsecutivo(v && v.serie,
                                     llaveDeRegistro(v && v.numero),
                                     v && v.fecha);
  if (!v) return null;
  // Sin número el papel sale con el id de antes: es preferible a no salir.
  v = { ...delNegocio(), ...v, numero: consecutivo || v.numero };
  return (
    <div className="capa-imprimir">
      <div className="barra-imprimir">
        <button className="boton suave" onClick={alCerrar}>Cerrar</button>
        <button className="boton" onClick={() => window.print()}>🖨️ Imprimir</button>
      </div>
      <div className="hoja">
        <Copia v={v} cual="Original — beneficiario" />
        <div className="corte" />
        <Copia v={v} cual="Copia — archivo" />
      </div>
    </div>
  );
}

export default Comprobante;
